import parseAddrs from 'email-addresses'

/**
 * Extracts valid email addresses from a string input.
 * @param {string} emails - A string containing one or more email addresses. Can be separated by commas, semicolons, or colons.
 * @returns {string[]} An array of valid email addresses extracted from the input string.
 * @description
 * This function takes a string of email addresses and parses them into an array of valid email addresses.
 * It handles various input formats:
 * - Comma-separated emails
 * - Semicolon-separated emails (converted to comma-separated)
 * - Colon-separated emails (typically used in "Name: email@example.com" format)
 *
 * The function uses the 'email-addresses' library to parse the input string.
 * It supports both individual email addresses and group formats.
 *
 * @example
 * const emails = "john@example.com, Jane Doe: jane@example.com; group@example.com"
 * const result = getEmailsFromString(emails);
 * // result: ["john@example.com", "jane@example.com", "group@example.com"]
 */
export const getEmailsFromString = (emails: string): string[] => {
	const input = emails.includes(':') ? emails : emails.replace(/;/g, ',')

	const addresses = parseAddrs({ input })?.addresses?.reduce(
		(acc, parsedAddr) => {
			if (parsedAddr.type === 'mailbox' && !!parsedAddr.address) {
				acc.push(parsedAddr.address)
			} else if (parsedAddr.type === 'group' && parsedAddr.addresses) {
				acc.push(...parsedAddr.addresses.map((add) => add.address))
			}

			return acc
		},
		[] as string[],
	)

	return addresses ? [...new Set(addresses)] : []
}
